import { useState } from "react";
import { Link } from "react-router-dom";
import { Banner, buttonClass } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatDate } from "@/lib/formatting/date-time";
import {
  STORE_CHARGE_SOURCE,
  STORE_CHARGE_STATUS,
  SUBSCRIPTION_PLAN_TYPE,
  type StoreKind,
  type StoreSubscriptionAssignmentView,
} from "@/lib/api/types";
import { useStoreSubscriptionLedgerQuery } from "@/shared/stores/services/queries/useStoreSubscriptionLedgerQuery";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The record's warning, at the top of the store drawer: a free trial that is
 * running (info) or has ended, or a billing period past its due date (warning).
 *
 * The admin API has no per-store restriction flag, so this reads the same
 * facts the backend's status service decides from (the newest assignment and
 * the oldest unpaid billing period — `store-subscription-status.service.ts`)
 * and states them, rather than claiming "restricted": a legacy Premium flag,
 * which this page cannot see, lifts the restriction. DEFAULT stores (the
 * tiered model) are never restricted, so they get no banner.
 */
export function StoreAccessBanner({
  storeId,
  storeKind,
  assignments,
  locale,
}: {
  storeId: string;
  storeKind: StoreKind;
  assignments: StoreSubscriptionAssignmentView[];
  locale: Locale;
}) {
  const t = useTranslations();
  // "Now" as of opening the record: stable across re-renders.
  const [now] = useState(() => Date.now());
  const assignment = storeKind === "DEFAULT" ? null : (assignments[0] ?? null);
  // Same query and cache entry as the subscriptions section below.
  const { data: ledger } = useStoreSubscriptionLedgerQuery({ storeId: assignment ? storeId : "" });
  if (!assignment) return null;

  const seeSubscriptions = (
    <Link
      to={`/${locale}/admin/stores/subscriptions?overdue=true`}
      className={buttonClass("secondary", "sm")}
      style={{ textDecoration: "none" }}
    >
      {t("admin.storeAccessSeeSubscriptions")}
    </Link>
  );

  if (assignment.type === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL) {
    const endsAt = new Date(new Date(assignment.startDate).getTime() + assignment.periodDays * MS_PER_DAY);
    const date = formatDate(endsAt.toISOString(), locale);
    if (now > endsAt.getTime()) {
      return (
        <Banner tone="warning" title={t("admin.storeAccessTrialEndedTitle")} actions={seeSubscriptions}>
          {t("admin.storeAccessTrialEndedText", { date })}
        </Banner>
      );
    }
    return (
      <Banner tone="info" title={t("admin.storeAccessTrialTitle", { date })}>
        {t("admin.storeAccessTrialText")}
      </Banner>
    );
  }

  const isPerOrder =
    assignment.type === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT || assignment.perOrderAmount !== null;
  if (isPerOrder || !ledger) return null;
  const current = ledger.schedules
    .map((item) => item.schedule)
    .filter(
      (charge) =>
        charge.source === STORE_CHARGE_SOURCE.SUBSCRIPTION_PERIOD &&
        (charge.status === STORE_CHARGE_STATUS.PENDING || charge.status === STORE_CHARGE_STATUS.PARTIAL),
    )
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];
  if (!current || now <= new Date(current.dueDate).getTime()) return null;

  const owed = Math.max(0, current.amountDue - current.paidAmount);
  let amount: string;
  try {
    amount = new Intl.NumberFormat(locale, { style: "currency", currency: current.currency || "EGP" }).format(owed);
  } catch {
    amount = `${current.currency} ${owed}`;
  }
  return (
    <Banner tone="warning" title={t("admin.storeAccessOverdueTitle")} actions={seeSubscriptions}>
      {t("admin.storeAccessOverdueText", {
        amount,
        plan: pickLocalized(locale, assignment.planNameAr, assignment.planNameEn),
        date: formatDate(current.dueDate, locale),
      })}
    </Banner>
  );
}
